import searchGraphql from './search.graphql';
import { useEffect, useState } from 'react';
import { SortByItemType } from 'src/components/formComponents/search/searchResults';
import { ApolloManager } from 'src/utils/apollo-manager';
import { RelevantPages, SupportedSearchContentTypes } from 'src/utils/common';
import { LinkHelper } from '../link-helper';

export type FilterCollection = {
	page?: Set<RelevantPages | string>;
	species?: Set<string>; //["Sheep", "Dairy Cattle"],
	supplyChain?: Set<string>; //["Domestic"],
	contentTypes?: Set<string>; //["Research Project"],
	languages?: Set<string>; //["English"],
	sort?: Set<SortByItemType | string>;
};

export interface SearchState {
	filters: FilterCollection;
	itemsPerPage: number;
}

export interface LinkState {
	query?: string;
	filters?: FilterCollection;
}

export enum ResultStatus {
	nothingSearched,
	searching,
	error,
	success,
}

export type ResultResponseItem = {
	contentful_id?: string;
	type?: SupportedSearchContentTypes; // Contentful's type: publicationResource, articleResource, ...
	resourceType?: string; // what the user may input as resource type, example: type: 'publicationResource' resourceType: 'Magazine'
	publicationDate?: string; // yyyy-MM-dd
	thumbnailLink: {
		title?: string;
		description?: string;
		imageSrc?: string;
		thumbnailIcon?: string;
	};
	externalUrl?: string;
	videoUrl?: string; //used for video resources so we can show a pop-up instead of navigating to the video details page.
	languages?: string[];
	title?: string;
	description?: string;
	link?: {
		url: string;
		text?: string;
	};
	icon?: string;
};

type ServerResponseItem = {
	contentful_id: string;
	type: SupportedSearchContentTypes; // Contentful's type: publicationResource, articleResource, ...
	articleType: string;
	externalArticleUrl?: string;
	projectType: string;
	publicationType: string;
	externalPublicationUrl?: string;
	reportType: string;
	publicationDate?: string; // yyyy-MM-dd
	thumbnailLink: {
		title: string;
		description?: string;
		thumbnailImage?: {
			fields: {
				file: {
					url: string;
				};
			};
		};
		thumbnailIcon?: string;
	};
	videoUrl?: string;
	languages?: string[];
	title?: string;
	description?: string;
	url?: string;
	otherType?: string;
	icon?: string;
};

type SearchQuery = {
	search?: {
		total: number;
		items: ServerResponseItem[];
	};
};

interface ResultResponse {
	items?: ResultResponseItem[];
	nextItemNumber?: number;
}

export interface SearchResult {
	status: ResultStatus;
	data?: ResultResponse;
}

type DataCallback = (data: SearchResult) => void;

const runQuery = async (
	query: string | undefined,
	filters: FilterCollection,
	itemsPerPage: number,
	skip: number = 0,
	callback: DataCallback
) => {
	const getFilterValues = (filter: Set<SortByItemType | string> | undefined) => {
		if (!filter || (filter && filter.size === 0)) {
			return undefined;
		} else {
			return Array.from(filter);
		}
	};

	const page = filters.page && filters.page.size > 0 ? Array.from(filters.page)[0] : undefined;
	const sort = filters.sort && filters.sort.size > 0 ? Array.from(filters.sort)[0] : undefined;

	const variables = {
		query,
		page,
		supplyChain: getFilterValues(filters.supplyChain),
		species: getFilterValues(filters.species),
		contentTypes: getFilterValues(filters.contentTypes),
		languages: getFilterValues(filters.languages),
		sort,
		skip: skip,
		limit: itemsPerPage,
	};

	try {
		callback({
			status: ResultStatus.searching,
		});
		const result = await ApolloManager.client.query<SearchQuery>({
			query: searchGraphql,
			variables,
		});

		let nextItemNr = undefined;

		if (result?.data?.search?.items?.length) {
			nextItemNr = skip + result.data.search.items.length;
			if (nextItemNr >= (result.data.search.total || 0)) {
				nextItemNr = undefined;
			}
		}

		const getResourceType = (item: ServerResponseItem | undefined) => {
			return (
				item &&
				(item.articleType ||
					item.projectType ||
					item.publicationType ||
					item.reportType ||
					item.otherType)
			);
		};

		const getLink = (item: ServerResponseItem): { url: string; text?: string } | undefined => {
			let url = LinkHelper.getLinkOfContentType(item.type, item.contentful_id);
			let text = item.videoUrl ? 'Watch Video' : 'Read More';
			switch (item.type) {
				case SupportedSearchContentTypes.Page:
					url = `/${item.url || '/'}`;
					break;
				case SupportedSearchContentTypes.IndustryStatisticsPanel:
				case SupportedSearchContentTypes.FormTab:
					url = item.url || '/';
					text = 'View Form';
					break;
				case SupportedSearchContentTypes.LinkWithDescription:
					url = item.url || '/';
					break;
			}
			return { url, text };
		};

		let items: ResultResponseItem[] =
			result.data?.search?.items
				?.filter((item) => item)
				.map<ResultResponseItem>((item) => {
					return {
						contentful_id: item.contentful_id,
						type: item.type,
						resourceType: getResourceType(item),
						publicationDate: item.publicationDate,
						thumbnailLink: {
							title: item.thumbnailLink?.title,
							description: item.thumbnailLink?.description,
							imageSrc: item.thumbnailLink?.thumbnailImage?.fields?.file?.url,
							thumbnailIcon: item.thumbnailLink?.thumbnailIcon,
						},
						videoUrl: item.videoUrl,
						languages: item.languages,
						externalUrl: item.externalArticleUrl || item.externalPublicationUrl,
						title: item.title,
						description: item.description,
						link: getLink(item),
						icon: item.icon,
					};
				}) || [];

		if (filters.languages && filters.languages.size > 0) {
			const filterLanguages = Array.from(filters.languages)
				.filter((fl) => fl) // filter falsy items
				.map((fl) => fl.toLowerCase());
			// when 'Other' or 'English' is selected, we return all (for now every resource is in English, and may be in additional languages, but always English.)
			if (!filterLanguages.some((fl) => fl === 'english')) {
				// if 'Other' is selected, return any resource that has additional languages, any languages
				if (filterLanguages.some((fl) => fl === 'other')) {
					items = items.filter((item) => item && item.languages && item.languages.length > 0);
				} else {
					// filter items that have the selected filter-language, so check that the item has a languages property and its value resembles the selected filter-language.
					// if item.languages[0] = 'Chinese (Mandarin)' and filters.languages[0] = 'Mandarin' it should be a match.
					items = items
						.filter((item) => item && item.languages && item.languages.length > 0)
						.filter((item) => {
							let allLanguages = item.languages!.join(' ');
							return filterLanguages.some((fl) => {
								const reg = new RegExp(fl, 'i');
								return allLanguages.search(reg) > -1;
							});
						});
				}
			}
		}

		callback({
			status: ResultStatus.success,
			data: {
				items,
				nextItemNumber: nextItemNr,
			},
		});
	} catch (err) {
		console.error(err);
		callback({
			status: ResultStatus.error,
		});
	}
};

export interface UseSearchManagerResult extends SearchState {
	query: string | undefined;
	result: SearchResult;
	hasFilter: (group: keyof FilterCollection, element: string) => boolean;
	loadMore: () => void;
	triggerSearch: () => void;
	clearAllFilters: () => void;
	onQueryChange: (newQueryStr: string) => void;
	queryFor: (theQuery: string) => void;
	onFilterChange: (
		filterGroup: keyof FilterCollection,
		filterElement: string | undefined,
		status: boolean,
		isExclusive: boolean
	) => void;
	setFilterCollection: (filterCollection: FilterCollection, triggerSearch?: boolean) => void;
	clearQuery: () => void;
}

const DEFAULT_ITEMS_PER_PAGE = 50;

export const useSearchManager = (
	itemsPerPage: number = DEFAULT_ITEMS_PER_PAGE
): UseSearchManagerResult => {
	const [query, setQuery] = useState<string | undefined>('');
	const [filters, setFilters] = useState<FilterCollection | undefined>(undefined);
	const [triggerSearchOnFilterChange, setTriggerSearchOnFilterChange] = useState(true);
	const [result, setResult] = useState({
		status: ResultStatus.nothingSearched,
	} as SearchResult);

	useEffect(() => {
		if (filters && triggerSearchOnFilterChange) {
			triggerSearch();
		}
	}, [filters]);

	const hasFilter = (group: keyof FilterCollection, element: string) => {
		const groupSet = filters && filters[group];

		if (!groupSet) return false;
		return groupSet.has(element);
	};

	const loadMore = () => {
		runQuery(query, filters || {}, itemsPerPage, result?.data?.nextItemNumber, (data) => {
			if (data && data.status === ResultStatus.success && data.data) {
				setResult((prevResult) => {
					let nextResult = JSON.parse(JSON.stringify(data)) as SearchResult;
					const prevItems = prevResult?.data?.items || [];
					const newItems = nextResult?.data?.items || [];
					nextResult.data = {
						...nextResult?.data,
						items: [...prevItems, ...newItems],
					};
					return nextResult;
				});
			}
		});
	};

	const triggerSearch = async () => {
		runQuery(query, filters || {}, itemsPerPage, 0, (data) => {
			setResult(data);
		});
	};

	const clearQuery = () => {
		setQuery('');
		setResult({
			status: ResultStatus.nothingSearched,
		});
	};

	const clearAllFilters = () => {
		setFilters({});
	};

	const onQueryChange = (nextQueryStr: string) => {
		setQuery(nextQueryStr);
	};

	const queryFor = (theQuery: string) => {
		setQuery(theQuery);
		// cannot setQuery and immediately call triggerSearch() (useState setters is asynchronous)
		runQuery(theQuery /* theQuery instead of query */, filters || {}, itemsPerPage, 0, (data) => {
			//order of setters here is not important
			setResult(data);
		});
	};

	const onFilterChange = (
		filterGroup: keyof FilterCollection,
		filterElement: string | undefined,
		status: boolean,
		isExclusive: boolean = false
	) => {
		setFilters((prevFilters) => {
			let newFilters = { ...prevFilters };
			if (!newFilters[filterGroup] || isExclusive) {
				newFilters[filterGroup] = new Set();
			}
			const current = newFilters[filterGroup];

			//filterElement may be undefined when resetting a dropdown menu to no value
			if (filterElement === undefined) {
				newFilters[filterGroup] = undefined;
			} else {
				if (status) {
					current?.add(filterElement);
				} else {
					current?.delete(filterElement);
				}
			}
			return newFilters;
		});
	};

	const setFilterCollection = (
		filterCollection: FilterCollection,
		triggerSearch: boolean = true
	) => {
		setTriggerSearchOnFilterChange(triggerSearch);
		setFilters({ ...filterCollection });
	};

	return {
		query,
		filters: filters || {},
		itemsPerPage,
		result,
		hasFilter,
		clearAllFilters,
		loadMore,
		triggerSearch,
		setFilterCollection,
		queryFor,
		onQueryChange,
		onFilterChange,
		clearQuery,
	};
};
